/**
 * Activity's bottom sheet — Board 13 C's numbers, which every sheet on the
 * phone and the iPad's portrait stop card share (`docs/activity.md` §10.1,
 * §10.2): the page dimmed at 38 % with its top 56 px still showing, a white
 * sheet with 20 px top corners, a 40 × 4 handle, a head with the title
 * (15.5/700) and a line under it and a 32 px round ✕, a body that scrolls,
 * and a footer whose buttons are 46 px and share the width.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { Glyph } from './glyphs.js'
import { useBackLayer } from './use-activity-history.js'

export function BottomSheet({
  title, sub, onClose, children, footer, label, bodyClassName, fill = false,
}: {
  title?: ReactNode
  sub?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** The dialog's name when the title is not plain text. */
  label: string
  bodyClassName?: string
  /** Up to the 56 px line whatever its contents (Board 13 C); else as tall as they are. */
  fill?: boolean
}) {
  const panel = useRef<HTMLDivElement>(null)
  /* A sheet is somewhere to have gone: back closes it (Aaron, Oct 26). */
  useBackLayer(onClose)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    panel.current?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-[70] flex flex-col" data-overlay-layer={70}>
      <button type="button" aria-label="Close" onClick={onClose}
        className={cn('w-full shrink-0 bg-[rgba(29,29,26,.38)]', fill ? 'h-14' : 'min-h-14 flex-1')} />
      <div ref={panel} role="dialog" aria-modal aria-label={label} tabIndex={-1}
        className={cn('flex min-h-0 flex-col overflow-hidden rounded-[20px_20px_0_0] bg-surface shadow-[0_-10px_30px_rgba(20,20,10,.12)] outline-none',
          fill ? 'flex-1' : 'max-h-[calc(100dvh-56px)]')}>
        <div className="flex justify-center pb-0.5 pt-2" aria-hidden>
          <i className="block h-1 w-10 rounded-full bg-line-strong" />
        </div>
        {title !== undefined && (
          <div className="flex items-center gap-3 border-b border-line-faint px-4 pb-3 pt-2.5">
            <div className="flex min-w-0 flex-col">
              <b className="truncate text-[15.5px]">{title}</b>
              {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
            </div>
            <span className="flex-1" />
            <button type="button" aria-label="Close" onClick={onClose}
              className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-line text-text-muted">
              <Glyph name="close" size={15} />
            </button>
          </div>
        )}
        <div className={cn('min-h-0 flex-1 overflow-y-auto', bodyClassName ?? 'px-4 py-3.5')}>{children}</div>
        {footer && (
          <div className="flex shrink-0 gap-2.5 border-t border-line px-4 pb-[18px] pt-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** A footer button (46 px, the width shared) — Board 13 C. */
export function SheetAction({
  children, onClick, primary = false, disabled = false,
}: { children: ReactNode; onClick: () => void; primary?: boolean; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={cn('inline-flex h-[46px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-[14px] font-semibold disabled:opacity-50',
        primary ? 'border-accent bg-accent text-white' : 'border-line bg-surface text-text')}>
      {children}
    </button>
  )
}
